import type { LineResult } from '@creanizer/types'
import '../styles/documentStyles.css'

interface LineViewerProps {
  document: LineResult
  imageAssets?: {
    avatars?: { [key: string]: string }
    headerImage?: string
  }
}

export function LineViewer({ document, imageAssets }: LineViewerProps) {
  // データ検証
  if (!document || !document.messages || document.messages.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">LINE会話</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // imageMappingからアバター画像を取得
  const getAvatarForUser = (sender: string): string | undefined => {
    if (!imageAssets?.avatars) return undefined
    
    // documentのimageMappingを参照
    const imageMapping = document.imageMapping || {}
    const originalName = imageMapping[sender]
    
    if (originalName && imageAssets.avatars[originalName]) {
      return imageAssets.avatars[originalName]
    }
    
    // フォールバック：直接マッチング
    return imageAssets.avatars[sender]
  }
  
  // 時刻フォーマット
  const formatTime = (timestamp: string): string => {
    try {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return timestamp
    }
  }
  
  // 日付の区切りを判定
  const shouldShowDateDivider = (currentMsg: any, prevMsg: any): boolean => {
    if (!prevMsg) return true
    const current = new Date(currentMsg.timestamp)
    const prev = new Date(prevMsg.timestamp)
    return current.toDateString() !== prev.toDateString()
  }
  
  // 日付フォーマット
  const formatDate = (timestamp: string): string => {
    try {
      const date = new Date(timestamp)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short'
      })
    } catch {
      return timestamp
    }
  }
  
  return (
    <div className="max-w-md mx-auto bg-[#7B9EBD] p-4 rounded-xl shadow-lg">
      {/* ヘッダー */}
      <div className="bg-[#273443] text-white rounded-t-lg p-3 mb-1 flex items-center justify-between">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-white rounded-full mr-3"></div>
          <div>
            <div className="font-semibold">
              {document.groupInfo?.name || document.title || '会話'}
            </div>
            {document.groupInfo && (
              <div className="text-xs text-gray-300">
                {document.groupInfo.memberCount}人
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-xl">🔍</span>
          <span className="text-xl">📞</span>
          <span className="text-xl">☰</span>
        </div>
      </div>
      
      {/* メッセージエリア */}
      <div className="bg-[#93AAC1] p-3 rounded-b-lg min-h-[400px] max-h-[600px] overflow-y-auto">
        {document.messages.map((message, index) => {
          const prevMessage = index > 0 ? document.messages[index - 1] : null
          const showDateDivider = shouldShowDateDivider(message, prevMessage)
          
          return (
            <div key={index}>
              {/* 日付区切り */}
              {showDateDivider && (
                <div className="text-center my-3">
                  <span className="bg-[#5B7288] text-white text-xs px-3 py-1 rounded-full">
                    {formatDate(message.timestamp)}
                  </span>
                </div>
              )}
              
              {/* メッセージ */}
              <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} mb-3`}>
                {/* アバター（自分以外） */}
                {!message.isUser && (
                  <div className="mr-2">
                    {getAvatarForUser(message.sender) ? (
                      <img
                        src={getAvatarForUser(message.sender)!}
                        alt={message.sender}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {message.sender.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                )}
                
                <div className={`max-w-[70%] ${message.isUser ? 'order-2' : 'order-1'}`}>
                  {/* 送信者名（自分以外） */}
                  {!message.isUser && (
                    <div className="text-xs text-white mb-1 ml-1">
                      {message.sender}
                    </div>
                  )}
                  
                  {/* メッセージバブル */}
                  <div className={`relative ${message.isUser ? 'ml-auto' : 'mr-auto'}`}>
                    <div
                      className={`px-3 py-2 rounded-2xl ${
                        message.isUser
                          ? 'bg-[#81C784] text-black'
                          : 'bg-white text-black'
                      } ${message.isSticker ? 'text-center p-4' : ''}`}
                    >
                      {message.isSticker ? (
                        <div className="text-4xl">
                          {message.stickerDescription || '😊'}
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap break-words text-sm">
                          {message.content}
                        </div>
                      )}
                    </div>
                    
                    {/* しっぽ */}
                    <div
                      className={`absolute top-2 ${
                        message.isUser
                          ? 'right-[-8px] border-l-[8px] border-l-[#81C784]'
                          : 'left-[-8px] border-r-[8px] border-r-white'
                      } border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent`}
                    ></div>
                  </div>
                  
                  {/* 時刻と既読 */}
                  <div className={`text-xs text-white mt-1 ${message.isUser ? 'text-right mr-1' : 'ml-1'}`}>
                    <span>{formatTime(message.timestamp)}</span>
                    {message.isUser && message.readBy && message.readBy.length > 0 && (
                      <span className="ml-2">既読 {message.readBy.length}</span>
                    )}
                  </div>
                  
                  {/* リアクション */}
                  {message.reactions && message.reactions.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {message.reactions.map((reaction, rIndex) => (
                        <span
                          key={rIndex}
                          className="bg-white/80 px-2 py-1 rounded-full text-xs"
                          title={reaction.by}
                        >
                          {reaction.emoji}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      
      {/* 入力エリア（装飾のみ） */}
      <div className="bg-white rounded-lg mt-2 p-2 flex items-center gap-2">
        <span className="text-gray-500">+</span>
        <span className="text-gray-500">📷</span>
        <span className="text-gray-500">😊</span>
        <div className="flex-1 bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-400">
          メッセージを入力
        </div>
        <span className="text-gray-500">🎤</span>
      </div>
    </div>
  )
}